import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { DeliveryLog } from '../types/domain';
import { FilterRule } from '../types/grid';
import { InnerLayout } from '../components/layout/InnerLayout';
import { HedgehogSpinner } from '../components/brand/HedgehogSpinner';
import { DeliveryLogsGrid } from '../components/delivery/DeliveryLogsGrid';
import { getGridSettings, saveGridSettings } from '../utils/gridPersistence';

export const DeliveryLogsView: React.FC = () => {
  const { projectId: routeProjectId } = useParams<{ projectId?: string }>();
  const { currentProject, canEdit } = useAuth();
  const { showToast } = useToast();
  const effectiveProjectId = routeProjectId || currentProject?.id;
  const persistenceKey = effectiveProjectId ? `delivery_logs_${effectiveProjectId}` : 'delivery_logs_global';

  const [logs, setLogs] = useState<DeliveryLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    try {
      const saved = localStorage.getItem('ericius_delivery_logs_page_size');
      if (saved) {
        const parsed = parseInt(saved, 10);
        if ([25, 50, 100, 250].includes(parsed)) return parsed;
      }
    } catch {
      // ignore
    }
    return 25;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [retryingLogId, setRetryingLogId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>(() => {
    const saved = getGridSettings(persistenceKey);
    return saved?.sortCol || 'created_at';
  });
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() => {
    const saved = getGridSettings(persistenceKey);
    return saved?.sortDir || 'desc';
  });
  const [sortRules, setSortRules] = useState<Array<{ id: string; desc: boolean }>>(() => {
    const saved = getGridSettings(persistenceKey);
    return saved?.sortRules || [];
  });
  const [filterRules, setFilterRules] = useState<FilterRule[]>(() => {
    const saved = getGridSettings(persistenceKey);
    return saved?.filterRules || [];
  });

  // Track previous project ID to only re-initialize settings when project actually changes
  const prevProjectIdRef = useRef<string | undefined>(effectiveProjectId);
  useEffect(() => {
    if (!effectiveProjectId) return;
    if (prevProjectIdRef.current === effectiveProjectId) return;
    prevProjectIdRef.current = effectiveProjectId;
    const key = `delivery_logs_${effectiveProjectId}`;
    const saved = getGridSettings(key);
    if (saved) {
      if (saved.sortCol) setSortBy(saved.sortCol);
      if (saved.sortDir) setSortDir(saved.sortDir);
      if (saved.sortRules) setSortRules(saved.sortRules);
      if (saved.filterRules) setFilterRules(saved.filterRules);
    } else {
      setSortBy('created_at');
      setSortDir('desc');
      setSortRules([]);
      setFilterRules([]);
    }
    setPage(1);
    setLogs([]);
    setTotal(0);
  }, [effectiveProjectId]);

  const fetchIdRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      fetchIdRef.current++;
    };
  }, []);

  const fetchDeliveryLogs = useCallback(
    async (isSilent = false, isManual = false) => {
      if (!effectiveProjectId) {
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      const currentFetchId = ++fetchIdRef.current;
      if (isManual) {
        setIsRefreshing(true);
      } else if (!isSilent) {
        setIsLoading(true);
      }

      try {
        let sortQuery = '';
        if (sortRules && sortRules.length > 0) {
          sortQuery = sortRules.map((s) => `${s.id}:${s.desc ? 'desc' : 'asc'}`).join(',');
        } else if (sortBy) {
          sortQuery = `${sortBy}:${sortDir}`;
        }

        const params: Record<string, any> = {
          page,
          limit: pageSize,
          sort: sortQuery,
          sort_by: sortBy,
          order: sortDir,
        };

        if (filterRules && filterRules.length > 0) {
          params.filters = JSON.stringify(filterRules);
          const statusRule = filterRules.find((r) => r.columnKey === 'status');
          if (statusRule && statusRule.value) {
            params.status = statusRule.value;
          }
        }

        const res = await apiClient<{ data: DeliveryLog[]; total: number }>(
          `/projects/${effectiveProjectId}/delivery-logs`,
          { params }
        );

        if (!isMountedRef.current || currentFetchId !== fetchIdRef.current) {
          return;
        }

        const nextLogs = res.data || [];
        setLogs((prev) => {
          if (prev.length === nextLogs.length) {
            let unchanged = true;
            for (let i = 0; i < prev.length; i++) {
              if (
                prev[i].id !== nextLogs[i].id ||
                prev[i].status !== nextLogs[i].status ||
                prev[i].status_code !== nextLogs[i].status_code
              ) {
                unchanged = false;
                break;
              }
            }
            if (unchanged) return prev;
          }
          return nextLogs;
        });

        setTotal((prev) => (prev === (res.total || 0) ? prev : res.total || 0));
      } catch (err: any) {
        if (!isMountedRef.current || currentFetchId !== fetchIdRef.current) {
          return;
        }
        console.warn('Could not fetch delivery logs from API:', err);
        setLogs([]);
        setTotal(0);
      } finally {
        if (isMountedRef.current && currentFetchId === fetchIdRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [effectiveProjectId, page, pageSize, sortBy, sortDir, sortRules, filterRules]
  );

  const handleFilterChange = useCallback(
    (newRules: FilterRule[]) => {
      setFilterRules((prev) => (JSON.stringify(prev) === JSON.stringify(newRules) ? prev : newRules));
      setPage(1);

      const key = effectiveProjectId ? `delivery_logs_${effectiveProjectId}` : 'delivery_logs_global';
      saveGridSettings(key, {
        filterRules: newRules,
      });
    },
    [effectiveProjectId]
  );

  const handleServerSortChange = (
    field: string,
    dir: 'asc' | 'desc' | '',
    allSorts?: Array<{ id: string; desc: boolean }>
  ) => {
    let nextRules: Array<{ id: string; desc: boolean }> = [];
    let nextSortBy = 'created_at';
    let nextSortDir: 'asc' | 'desc' = 'desc';

    if (allSorts && allSorts.length > 0) {
      nextRules = allSorts;
      nextSortBy = allSorts[0].id;
      nextSortDir = allSorts[0].desc ? 'desc' : 'asc';
    } else if (field && dir) {
      nextSortBy = field;
      nextSortDir = dir;
      nextRules = [{ id: field, desc: dir === 'desc' }];
    } else {
      nextSortBy = 'created_at';
      nextSortDir = 'desc';
      nextRules = [];
    }

    setSortRules((prev) => (JSON.stringify(prev) === JSON.stringify(nextRules) ? prev : nextRules));
    setSortBy((prev) => (prev === nextSortBy ? prev : nextSortBy));
    setSortDir((prev) => (prev === nextSortDir ? prev : nextSortDir));
    setPage(1);

    const key = effectiveProjectId ? `delivery_logs_${effectiveProjectId}` : 'delivery_logs_global';
    saveGridSettings(key, {
      sortCol: nextSortBy,
      sortDir: nextSortDir,
      sortRules: nextRules,
    });
  };

  useEffect(() => {
    fetchDeliveryLogs(false);
  }, [fetchDeliveryLogs]);

  // Auto-refresh when window regains focus
  useEffect(() => {
    const handleFocus = () => {
      fetchDeliveryLogs(true, false);
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchDeliveryLogs]);

  // Periodic bounded poll every 5s
  useEffect(() => {
    if (!effectiveProjectId) return;
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchDeliveryLogs(true, false);
    }, 5000);
    return () => clearInterval(interval);
  }, [effectiveProjectId, fetchDeliveryLogs]);

  const handleRefresh = () => {
    fetchDeliveryLogs(true, true);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
    try {
      localStorage.setItem('ericius_delivery_logs_page_size', String(newSize));
    } catch {
      // ignore
    }
  };

  const handleRetry = async (logId: string) => {
    if (!effectiveProjectId) return;
    setRetryingLogId(logId);

    try {
      const res = await apiClient<{ message: string; job_id?: string }>(
        `/projects/${effectiveProjectId}/delivery-logs/${logId}/retry`,
        { method: 'POST' }
      );

      showToast({
        type: 'success',
        title: 'Delivery Retry Queued',
        message: res.message || 'Notification job has been scheduled for immediate re-execution.',
      });

      await fetchDeliveryLogs(true);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Retry Failed',
        message: err?.message || 'Failed to re-queue notification delivery job.',
      });
    } finally {
      setRetryingLogId(null);
    }
  };


  return (
    <InnerLayout
      title="Delivery Logs"
      description="Audit trail of asynchronous notification dispatches with real-time error details and manual retry controls."
      variant="fullHeight"
    >
      {/* Main Delivery Logs DataGrid */}
      <div className="flex-1 min-h-0 min-w-0 w-full max-w-full overflow-hidden flex flex-col">
        <DeliveryLogsGrid
          logs={logs}
          totalLogs={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          isLoading={isLoading}
          canEdit={canEdit}
          onRetry={handleRetry}
          retryingLogId={retryingLogId}
          onServerSortChange={handleServerSortChange}
          onServerFilterChange={handleFilterChange}
          filterRules={filterRules}
          sortBy={sortBy}
          sortDir={sortDir}
          sortRules={sortRules}
          persistenceKey={persistenceKey}
        />
      </div>
    </InnerLayout>
  );
};
